import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { Observable, Observer } from 'rxjs';

export interface IGeolocalizacao {
  latitude: number;
  longitude: number;
}

const OPCOES_GPS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0,
};

const ERRO_PERMISSAO = 1;
const ERRO_INDISPONIVEL = 2;
const ERRO_TIMEOUT = 3;

@Injectable({
  providedIn: 'root',
})
export class GeolocationService {
  private notificacaoService = inject(NotificacaoService);
  private translate = inject(TranslateService);

  getCurrentPosition(): Observable<IGeolocalizacao> {
    return new Observable((observer: Observer<IGeolocalizacao>) => {
      const geolocation = this.obterGeolocation();

      if (!geolocation) {
        this.notificarFalha('GEOLOCALIZACAO.ERRO_NAO_SUPORTADA');
        observer.complete();
        return;
      }

      geolocation.getCurrentPosition(
        (position) => {
          observer.next(this.paraCoordenadas(position));
          observer.complete();
        },
        (error) => {
          this.notificarFalha(this.chaveErro(error.code));
          observer.complete();
        },
        OPCOES_GPS
      );
    });
  }

  watchPosition(): Observable<IGeolocalizacao> {
    return new Observable((observer: Observer<IGeolocalizacao>) => {
      const geolocation = this.obterGeolocation();

      if (!geolocation) {
        this.notificarFalha('GEOLOCALIZACAO.ERRO_NAO_SUPORTADA');
        observer.complete();
        return;
      }

      const watchId = geolocation.watchPosition(
        (position) => observer.next(this.paraCoordenadas(position)),
        (error) => {
          this.notificarFalha(this.chaveErro(error.code));
          observer.complete();
        },
        OPCOES_GPS
      );

      return () => geolocation.clearWatch(watchId);
    });
  }

  private obterGeolocation(): Geolocation | null {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return null;
    }

    return navigator.geolocation;
  }

  private paraCoordenadas(position: GeolocationPosition): IGeolocalizacao {
    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };
  }

  private notificarFalha(chave: string): void {
    this.notificacaoService.notificacaoError(this.translate.instant(chave));
  }

  private chaveErro(codigo: number): string {
    switch (codigo) {
      case ERRO_PERMISSAO:
        return 'GEOLOCALIZACAO.ERRO_PERMISSAO';
      case ERRO_INDISPONIVEL:
        return 'GEOLOCALIZACAO.ERRO_INDISPONIVEL';
      case ERRO_TIMEOUT:
        return 'GEOLOCALIZACAO.ERRO_TIMEOUT';
      default:
        return 'GEOLOCALIZACAO.ERRO_GENERICO';
    }
  }
}
